import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import moment from "moment";
import * as React from "react";
import { FC, useCallback, useEffect, useMemo, useState } from "react";
import { connect } from "react-redux";
import { useParams } from "react-router";
import { bindActionCreators } from "redux";
import { InlineLoading } from "src/components/common";
import ToggleButton from "src/components/common/Buttons/ToggleButton";
import Tree, { ITree } from "src/components/common/Tree/Tree";
import { Link } from "src/components/routing";
import { FontAwesomeIcons } from "src/constants/fontAwesomeIcons";
import { TrialInstitutionLaboratoryPersonnelsBreadcrumbs } from "src/helpers/breadcrumbHelper";
import useNavigate from "src/hooks/useNavigate";
import { TogglePageItem } from "src/reducers/pages/togglePage";
import * as NavigationActions from "../../../actions/navigation";
import * as TrialInstitutionLaboratoryActions from "../../../actions/trialInstitutionLaboratory";
import * as TrialInstitutionLaboratoryPersonnelActions from "../../../actions/trialInstitutionLaboratoryPersonnels";
import { RestrictedLayout } from "../../../components/layouts/index";
import * as Dtos from "../../../dtos/Tmd.dtos";
import { RequestState } from "../../../enumerations/RequestState";
import * as AuthenticationHelper from "../../../helpers/authentication";
import { TrialInstitutionLabSubMenu } from "../../../helpers/subMenuHelper";
import { ITmdState } from "../../../reducers/index";

import "./TrialInstitutionLaboratoryPersonnelsPage.scss";

interface TrialInstitutionLaboratoryPersonnelsPageProps {
    trialInstitutionLaboratory: Dtos.TrialInstitutionLaboratory;
    loadingTrialInstitutionLaboratory: boolean;
    loadTrialInstitutionLaboratorySuccess: boolean;
    loadTrialInstitutionLaboratoryFailure: boolean;
    loadTrialInstitutionLaboratoryErrorCode: string;
    trialInstitutionLaboratoryPersonnel: Dtos.TrialInstitutionLaboratoryPersonnel[];
    loadingTrialInstitutionLaboratoryPersonnel: boolean;
    loadTrialInstitutionLaboratoryPersonnelSuccess: boolean;
    loadTrialInstitutionLaboratoryPersonnelFailure: boolean;
    loadTrialInstitutionLaboratoryPersonnelErrorCode: string;
    permissions?: Dtos.PersonnelTmdPermissions[];
    pageSize: number;
    toggleItems: TogglePageItem[];
    clearTrialInstitutionLaboratory: TrialInstitutionLaboratoryActions.ITrialInstitutionLaboratoryClearActionCreator;
    loadTrialInstitutionLaboratory: TrialInstitutionLaboratoryActions.ITrialInstitutionLaboratoryLoadByIdActionCreator;
    clearTrialInstitutionLaboratoryPersonnel: TrialInstitutionLaboratoryPersonnelActions.ITrialInstitutionLaboratoryPersonnelClearActionCreator;
    loadTrialInstitutionLaboratoryPersonnel: TrialInstitutionLaboratoryPersonnelActions.ITrialInstitutionLaboratoryPersonnelLoadByLaboratoryIdActionCreator;
    setBreadcrumbs: NavigationActions.ISetBreadcrumbsActionCreator;
    setDrawerItems: NavigationActions.ISetDrawerItemsActionCreator;
}

const TrialInstitutionLaboratoryPersonnelsPage: FC<
    TrialInstitutionLaboratoryPersonnelsPageProps
> = ({
    trialInstitutionLaboratory,
    loadingTrialInstitutionLaboratory,
    loadTrialInstitutionLaboratorySuccess,
    loadTrialInstitutionLaboratoryFailure,
    loadTrialInstitutionLaboratoryErrorCode,
    trialInstitutionLaboratoryPersonnel,
    loadingTrialInstitutionLaboratoryPersonnel,
    loadTrialInstitutionLaboratoryPersonnelSuccess,
    loadTrialInstitutionLaboratoryPersonnelFailure,
    loadTrialInstitutionLaboratoryPersonnelErrorCode,
    permissions,
    clearTrialInstitutionLaboratory,
    loadTrialInstitutionLaboratory,
    clearTrialInstitutionLaboratoryPersonnel,
    loadTrialInstitutionLaboratoryPersonnel,
    setBreadcrumbs,
    setDrawerItems
}) => {
    //
    // -- Params --
    const { trialId } = useParams<{ trialId: string }>();
    const { trialInstitutionId } = useParams<{ trialInstitutionId: string }>();
    const { trialInstitutionLaboratoryId } = useParams<{
        trialInstitutionLaboratoryId: string;
    }>();
    const trialIdNumber = parseInt(trialId, 10);
    const trialInstitutionIdNumber = parseInt(trialInstitutionId, 10);
    const trialInstitutionLaboratoryIdNumber = parseInt(
        trialInstitutionLaboratoryId,
        10
    );

    //
    // -- Hooks --
    const navigate = useNavigate();

    //
    // -- States --
    const [showCeased, setShowCeased] = useState<boolean>(false);
    const [treeData, setTreeData] = useState<ITree | null>(null);
    const [initalizedTreeData, setInitalizedTreeData] =
        useState<boolean>(false);
    const [toggleState, setToggleState] = useState<string[]>([]);
    const [creatingTree, setCreatingTree] = useState<boolean>(false);

    //
    // -- Variables --
    const showPersonnels =
        !loadingTrialInstitutionLaboratory &&
        loadTrialInstitutionLaboratorySuccess &&
        !loadingTrialInstitutionLaboratoryPersonnel &&
        loadTrialInstitutionLaboratoryPersonnelSuccess && 
        !creatingTree;


    // Load Trial Institution Laboratory & Personnel
    useEffect(() => {
        if (
            trialIdNumber &&
            trialInstitutionIdNumber &&
            trialInstitutionLaboratoryIdNumber &&
            !trialInstitutionLaboratory &&
            !loadingTrialInstitutionLaboratory
        ) {
            setBreadcrumbs(
                TrialInstitutionLaboratoryPersonnelsBreadcrumbs(
                    trialIdNumber,
                    trialInstitutionIdNumber,
                    trialInstitutionLaboratoryIdNumber
                )
            );

            setTreeData(null);

            loadTrialInstitutionLaboratory(trialInstitutionLaboratoryIdNumber);
            loadTrialInstitutionLaboratoryPersonnel(trialInstitutionLaboratoryIdNumber);
        }
    }, [
        trialIdNumber,
        trialInstitutionIdNumber,
        trialInstitutionLaboratoryIdNumber,
        trialInstitutionLaboratory,
        permissions,
        setDrawerItems,
        setBreadcrumbs,
        loadTrialInstitutionLaboratory,
        TrialInstitutionLaboratoryPersonnelsBreadcrumbs,
        setTreeData,
        loadTrialInstitutionLaboratoryPersonnel
    ]);

    // Navigate to error page on load failure
    useEffect(() => {
        if (loadTrialInstitutionLaboratoryFailure) {
            navigate(`/error/${loadTrialInstitutionLaboratoryErrorCode}`);
        }
        if (loadTrialInstitutionLaboratoryPersonnelFailure) {
            navigate(`/error/${loadTrialInstitutionLaboratoryPersonnelErrorCode}`);
        }
    }, [
        loadTrialInstitutionLaboratoryFailure,
        loadTrialInstitutionLaboratoryErrorCode,
        loadTrialInstitutionLaboratoryPersonnelFailure,
        loadTrialInstitutionLaboratoryPersonnelErrorCode,
        navigate,
    ]);

    // Update breadcrumbs
    useEffect(() => {
        if (
            trialIdNumber &&
            trialInstitutionIdNumber &&
            trialInstitutionLaboratoryIdNumber &&
            trialInstitutionLaboratory
        ) {
            setDrawerItems(
                TrialInstitutionLabSubMenu(
                    trialInstitutionLaboratory,
                    permissions
                )
            );
            setBreadcrumbs(
                TrialInstitutionLaboratoryPersonnelsBreadcrumbs(
                    trialIdNumber,
                    trialInstitutionIdNumber,
                    trialInstitutionLaboratoryIdNumber,
                    trialInstitutionLaboratory?.trialInstitution?.trial
                        ?.studyAcronym,
                    trialInstitutionLaboratory?.trialInstitution?.institution
                        ?.name,
                    trialInstitutionLaboratory?.laboratory?.name
                )
            );
        }
    }, [
        trialIdNumber,
        trialInstitutionIdNumber,
        trialInstitutionLaboratoryIdNumber,
        trialInstitutionLaboratory,
        permissions,
        setDrawerItems,
        setBreadcrumbs,
        TrialInstitutionLabSubMenu,
        TrialInstitutionLaboratoryPersonnelsBreadcrumbs,
    ]);

    // Cleanup effect to clear the laboratory on component unmount
    useEffect(() => {
        return () => {
            setTreeData(null);
            clearTrialInstitutionLaboratory();
            clearTrialInstitutionLaboratoryPersonnel();
        };
    }, [clearTrialInstitutionLaboratory, clearTrialInstitutionLaboratoryPersonnel, setTreeData]);

    const onChange = (newValue) => {
        setInitalizedTreeData(false);
        setShowCeased(newValue);
    };

    // Create tree
    useEffect(() => {
        if (trialInstitutionLaboratoryPersonnel && !initalizedTreeData) {
            setCreatingTree(true);

            // If no trial institution laboratory personnel return
            if (trialInstitutionLaboratoryPersonnel?.length == 0) {
                setCreatingTree(false);
                return;
            }

            let uniqueRoles: Dtos.Role[] = [];
            if (showCeased) {
                uniqueRoles = Array.from(
                    new Map(
                        trialInstitutionLaboratoryPersonnel?.map((x) => [
                            x.role.id,
                            x.role,
                        ])
                    ).values()
                );
            } else {
                uniqueRoles = Array.from(
                    new Map(
                        trialInstitutionLaboratoryPersonnel
                            ?.filter((x) => !x.dateCeased)
                            .map((x) => [x.role.id, x.role])
                    ).values()
                );
            }

            const createChildNodes = (roleId, showCeased) => {
                let children = [];

                if (showCeased) {
                    children =
                        trialInstitutionLaboratoryPersonnel
                            ?.filter((y) => y.roleId == roleId)
                            ?.sort((a, b) => a?.id - b?.id)
                            ?.map((y) => ({
                                id: `personnel-${y.id}`,
                                data: (
                                    <div
                                        className={`form-row ${
                                            y?.dateCeased ? "ceased" : ""
                                        }`}
                                    >
                                        <div className="col-1">
                                            <FontAwesomeIcon
                                                icon={
                                                    FontAwesomeIcons.Duotone
                                                        .USER
                                                }
                                            />
                                        </div>
                                        <div className="col">
                                            {
                                                y?.personnel
                                                    ?.displayWithSalutation
                                            }
                                        </div>
                                        <div className="col">
                                            {y?.dateStarted
                                                ? moment(
                                                        y?.dateStarted,
                                                        "YYYY-MM-DD HH:mm:ss.SSSSSSS Z"
                                                    ).format("DD/MM/YYYY")
                                                : ""}
                                        </div>
                                        <div className="col">
                                            {y?.dateCeased
                                                ? moment(
                                                        y?.dateCeased,
                                                        "YYYY-MM-DD HH:mm:ss.SSSSSSS Z"
                                                    ).format("DD/MM/YYYY")
                                                : ""}
                                        </div>
                                    </div>
                                ),
                                icon: null,
                                collapsible: false,
                            })) ?? [];
                } else {
                    children =
                        trialInstitutionLaboratoryPersonnel
                            ?.filter((y) => y.roleId == roleId && !y.dateCeased)
                            ?.sort((a, b) => a?.id - b?.id)
                            ?.map((y) => ({
                                id: `personnel-${y?.id}`,
                                data: (
                                    <div className="form-row">
                                        <div className="col-1">
                                            <FontAwesomeIcon
                                                icon={
                                                    FontAwesomeIcons.Duotone
                                                        .USER
                                                }
                                            />
                                        </div>
                                        <div className="col">
                                            {
                                                y?.personnel
                                                    ?.displayWithSalutation
                                            }
                                        </div>
                                        <div className="col">
                                            {y?.dateStarted
                                                ? moment(
                                                        y?.dateStarted,
                                                        "YYYY-MM-DD HH:mm:ss.SSSSSSS Z"
                                                    ).format("DD/MM/YYYY")
                                                : ""}
                                        </div>
                                        <div className="col">
                                            {y?.dateCeased
                                                ? moment(
                                                        y?.dateCeased,
                                                        "YYYY-MM-DD HH:mm:ss.SSSSSSS Z"
                                                    ).format("DD/MM/YYYY")
                                                : ""}
                                        </div>
                                    </div>
                                ),
                                icon: null,
                                collapsible: false,
                            })) ?? [];
                }

                // Add table header
                if (children?.length > 0) {
                    children.unshift({
                        id: `tableheader`,
                        data: (
                            <div className="form-row">
                                <div className="col-1"></div>
                                <div className="col">
                                    <strong>Name</strong>
                                </div>
                                <div className="col">
                                    <strong>Date Started</strong>
                                </div>
                                <div className="col">
                                    <strong>Date Ceased</strong>
                                </div>
                            </div>
                        ),
                        icon: null,
                        collapsible: false,
                    });
                }

                return children;
            };

            let treeNodes =
                uniqueRoles
                    ?.sort((a, b) => a?.value?.localeCompare(b?.value))
                    ?.map((x) => ({
                        id: `role-${x.id}`,
                        data: (
                            <div className="d-flex align-items-center">
                                <FontAwesomeIcon
                                    icon={FontAwesomeIcons.Duotone.ROLE}
                                />
                                <span
                                    className="pl-4 mb-0"
                                    style={{ width: "275px" }}
                                >
                                    {x?.value}
                                </span>
                                <div
                                    className="d-flex align-items-center justify-content-center"
                                    style={{ width: "50px" }}
                                >
                                    <span className="badge badge-pill badge-secondary ml-4 mb-0">
                                        {
                                            trialInstitutionLaboratory.personnels?.filter(
                                                (y) =>
                                                    showCeased
                                                        ? y.roleId == x.id
                                                        : y.roleId == x.id &&
                                                            !y.dateCeased
                                            )?.length
                                        }
                                    </span>
                                </div>
                            </div>
                        ),
                        icon: null,
                        collapsible: true,
                        subTree: {
                            children: [...createChildNodes(x.id, showCeased)],
                        },
                    })) ?? [];

            // Initialize tree toggle
            const expanded = treeNodes.map((z) => z.id);

            setToggleState(expanded);
            setTreeData({ children: treeNodes });
            setInitalizedTreeData(true);
            setCreatingTree(false);
        }
    }, [
        trialInstitutionLaboratoryPersonnel,
        initalizedTreeData,
        treeData,
        setTreeData,
        setToggleState,
        setInitalizedTreeData,
        showCeased,
        setCreatingTree,
    ]);

    const handleToggle = useCallback(
        (id: string, navigateFlag?: boolean) => {
            if (navigateFlag) {
                if (
                    id.includes("personnel") &&
                    AuthenticationHelper.hasPermission(
                        permissions,
                        Dtos.Permission.TrialPersonnelRoleView
                    )
                ) {
                    navigate(
                        `/trials/${trialIdNumber}/trialinstitution/${trialInstitutionIdNumber}/labs/${trialInstitutionLaboratoryIdNumber}/personnel/${id.replace(
                            "personnel-",
                            ""
                        )}/details`
                    );
                }
                if (id.includes("role")) {
                    setToggleState((prev) =>
                        prev.includes(id)
                            ? prev.filter((nodeId) => nodeId !== id)
                            : [...prev, id]
                    );
                }
            } else {
                setToggleState((prev) =>
                    prev.includes(id)
                        ? prev.filter((nodeId) => nodeId !== id)
                        : [...prev, id]
                );
            }
        },
        [
            setToggleState,
            navigate,
            trialIdNumber,
            trialInstitutionIdNumber,
            permissions,
        ]
    );

    // Refresh tree
    useEffect(() => {
        if (trialInstitutionLaboratoryPersonnel) {
            setInitalizedTreeData(false);
        }
    }, [trialInstitutionLaboratoryPersonnel, setInitalizedTreeData]);

    const renderContent = useMemo(() => {
        return (
            <div>
                <div className="row">
                    <div className="col-md-12 col-lg-auto mb-2">
                        <h1>Personnel</h1>
                    </div>
                    <div
                        className="col-md-12 col-lg d-flex justify-content-md-start justify-content-lg-end align-items-stretch mb-2 mb-lg-0"
                        style={{ height: "38px" }}
                    >
                        <ToggleButton
                            id="toggle-button-ceased"
                            checked={showCeased}
                            onChange={onChange}
                            labelA="Hide ceased personnel"
                            labelB="Show ceased personnel"
                            style={{ minWidth: "200px" }}
                        />
                        <a
                            className="btn btn-info mr-2"
                            target="_blank"
                            href={encodeURI(
                                `/api/personnelrole/export/${trialInstitutionLaboratory?.trialInstitution?.trialId}/${trialInstitutionLaboratory?.trialInstitution?.institutionId}/0/${trialInstitutionLaboratory?.laboratoryId}`
                            )}
                        >
                            <FontAwesomeIcon icon={FontAwesomeIcons.Solid.CLOUD_DOWNLOAD} fixedWidth />
                        </a>
                        {AuthenticationHelper.hasPermission(
                            permissions,
                            Dtos.Permission.TrialInstitutionLabCreate
                        ) && (
                            <Link
                                className={"btn btn-primary"}
                                url={`/trials/${trialInstitutionLaboratory?.trialInstitution?.trialId}/trialinstitution/${trialInstitutionLaboratory?.trialInstitution?.id}/labs/${trialInstitutionLaboratory?.id}/personnel/create`}
                            >
                                Create
                            </Link>
                        )}
                    </div>
                </div>
                {!showPersonnels ? (
                    <InlineLoading absoluteCenter={true} size={50} />
                ) : null}
                {showPersonnels ? (
                    <>
                        {treeData && treeData?.children?.length > 0 ? (
                            <Tree
                                treeData={treeData}
                                onToggle={handleToggle}
                                toggleState={toggleState}
                            />
                        ) : (
                            <div className="card card-shadow p-3">
                                <div className="alert alert-info d-flex mb-0">
                                    <div className="mr-2">
                                        <FontAwesomeIcon
                                            icon={
                                                FontAwesomeIcons.Solid
                                                    .INFO_SQUARE
                                            }
                                            fixedWidth
                                        />
                                    </div>
                                    <span>
                                        There are currently no trial institution
                                        laboratory personnel.
                                    </span>
                                </div>
                            </div>
                        )}
                    </>
                ) : null}
            </div>
        );
    }, [
        AuthenticationHelper,
        permissions,
        showPersonnels,
        trialInstitutionLaboratory,
        treeData,
        handleToggle,
        toggleState,
        onChange,
    ]);

    return (
        <RestrictedLayout
            permission={Dtos.Permission.TrialInstitutionLabView}
            loading={loadingTrialInstitutionLaboratory || loadingTrialInstitutionLaboratoryPersonnel}
        >
            {renderContent}
        </RestrictedLayout>
    );
};

const mapStateToProps = (
    state: ITmdState
): Partial<TrialInstitutionLaboratoryPersonnelsPageProps> => {
    let trialInstitutionLaboratory:
        | Dtos.TrialInstitutionLaboratory
        | undefined = !(
        state.trialInstitutionLaboratory.formData instanceof Array
    )
        ? state.trialInstitutionLaboratory.formData
        : undefined;
    let trialInstitutionLaboratoryPersonnel:
        | Dtos.TrialInstitutionLaboratoryPersonnel[] | undefined = (state.trialInstitutionLaboratoryPersonnel.formData instanceof Array)
        ? state.trialInstitutionLaboratoryPersonnel.formData
        : undefined;
    let permissions = state.user.userSummary
        ? state.user.userSummary.permissions
        : [];

    return {
        trialInstitutionLaboratory: trialInstitutionLaboratory!,
        loadingTrialInstitutionLaboratory:
            state.trialInstitutionLaboratory.loadState &&
            state.trialInstitutionLaboratory.loadState.status ==
                RequestState.Pending,
        loadTrialInstitutionLaboratorySuccess:
            state.trialInstitutionLaboratory.loadState &&
            state.trialInstitutionLaboratory.loadState.status ==
                RequestState.Success,
        loadTrialInstitutionLaboratoryFailure:
            state.trialInstitutionLaboratory.loadState &&
            state.trialInstitutionLaboratory.loadState.status ==
                RequestState.Failure,
        loadTrialInstitutionLaboratoryErrorCode:
            state.trialInstitutionLaboratory.loadState.errorCode,
        trialInstitutionLaboratoryPersonnel: trialInstitutionLaboratoryPersonnel,
        loadingTrialInstitutionLaboratoryPersonnel:
            state.trialInstitutionLaboratoryPersonnel.loadState &&
            state.trialInstitutionLaboratoryPersonnel.loadState.status ==
                RequestState.Pending,
        loadTrialInstitutionLaboratoryPersonnelSuccess:
            state.trialInstitutionLaboratoryPersonnel.loadState &&
            state.trialInstitutionLaboratoryPersonnel.loadState.status ==
                RequestState.Success,
        loadTrialInstitutionLaboratoryPersonnelFailure:
            state.trialInstitutionLaboratoryPersonnel.loadState &&
            state.trialInstitutionLaboratoryPersonnel.loadState.status ==
                RequestState.Failure,
        loadTrialInstitutionLaboratoryPersonnelErrorCode:
            state.trialInstitutionLaboratoryPersonnel.loadState.errorCode,
        permissions: permissions,
    };
};

const mapDispatchToProps = (
    dispatch
): Partial<TrialInstitutionLaboratoryPersonnelsPageProps> => {
    return {
        loadTrialInstitutionLaboratory: bindActionCreators(
            TrialInstitutionLaboratoryActions.LoadTrialInstitutionLaboratoryById,
            dispatch
        ),
        clearTrialInstitutionLaboratory: bindActionCreators(
            TrialInstitutionLaboratoryActions.Clear,
            dispatch
        ),
        loadTrialInstitutionLaboratoryPersonnel: bindActionCreators(
            TrialInstitutionLaboratoryPersonnelActions.LoadTrialInstitutionLaboratoryPersonnelByLaboratoryId,
            dispatch
        ),
        clearTrialInstitutionLaboratoryPersonnel: bindActionCreators(
            TrialInstitutionLaboratoryPersonnelActions.Clear,
            dispatch
        ),
        setBreadcrumbs: bindActionCreators(
            NavigationActions.SetBreadcrumbs,
            dispatch
        ),
        setDrawerItems: bindActionCreators(
            NavigationActions.SetDrawerItems,
            dispatch
        )
    };
};

export default connect(
    mapStateToProps,
    mapDispatchToProps
)(TrialInstitutionLaboratoryPersonnelsPage);
